import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { sendChatMessage } from "../lib/api";

function useSessionId() {
  const [id, setId] = useState("");
  useEffect(() => {
    let s = localStorage.getItem("logdrive_session");
    if (!s) {
      s =
        (crypto.randomUUID && crypto.randomUUID()) ||
        `sess-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem("logdrive_session", s);
    }
    setId(s);
  }, []);
  return id;
}

const INTRO = {
  id: "intro",
  role: "assistant",
  content:
    "Partnership Assistant online. Ask about the LogDrive technology, applications, or the path to a briefing. Operating under NDA-ready protocols.",
};

let _msgCounter = 0;
const nextMsgId = () => `msg-${Date.now()}-${++_msgCounter}`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const session_id = useSessionId();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  async function onSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy || !session_id) return;
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { reply } = await sendChatMessage(session_id, text);
      setMessages((m) => [...m, { id: nextMsgId(), role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          id: nextMsgId(),
          role: "assistant",
          content:
            "Connection interrupted. Try again, or send a partnership inquiry from the Contact page.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  }

  const suggestions = [
    "What is the LogDrive?",
    "How does it compare to planetary gearboxes?",
    "V-22 Osprey application?",
    "How do we start a partnership?",
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-20 right-6 z-50 inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] text-white font-mono-tech uppercase tracking-widest text-[11px] px-4 py-3 border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
        aria-label="Toggle partnership assistant"
      >
        {open ? <X size={14} /> : <MessageSquare size={14} />}
        <span className="hidden sm:inline">
          {open ? "Close assistant" : "Partnership assistant"}
        </span>
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-36 right-6 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-10rem))] flex flex-col border border-white/15 bg-black shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#0a0a0a]">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inset-0 bg-[#FF3B30] animate-ping opacity-60" />
                <span className="relative inline-block h-2 w-2 bg-[#FF3B30]" />
              </span>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70">
                partnership_assistant // claude-sonnet-4.5
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-white/60 hover:text-white"
              data-testid="chat-close-btn"
            >
              <X size={14} />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm"
            data-testid="chat-messages"
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 whitespace-pre-wrap leading-relaxed border ${
                    m.role === "user"
                      ? "bg-white text-black border-white"
                      : "bg-[#0a0a0a] text-white/90 border-white/10"
                  }`}
                >
                  <div className="font-mono-tech text-[9px] uppercase tracking-widest mb-1 opacity-60">
                    {m.role === "user" ? "you" : "assistant"}
                  </div>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-white/50 text-xs font-mono-tech">
                <Loader2 size={12} className="animate-spin" />
                Processing…
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setInput(s)}
                  data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                  className="text-[11px] font-mono-tech uppercase tracking-wider border border-white/15 px-2 py-1 text-white/70 hover:text-white hover:border-white/40"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={onSend}
            className="border-t border-white/10 p-2 flex gap-2"
            data-testid="chat-form"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about the technology, applications, or partnership path…"
              className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none font-mono-tech"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-40 text-white"
              data-testid="chat-send-btn"
              aria-label="Send"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
